import React from 'react';
import { Link, useParams, useNavigate, Navigate } from 'react-router-dom';
import Seo from '../components/Seo';
import { Button } from '../components/ui/button';
import { ArrowLeft, ArrowRight, CaretRight } from '@phosphor-icons/react';
import { getPostBySlug, getAllPosts } from '../data/blogPosts';

function renderBlock(block, i) {
  switch (block.type) {
    case 'h2':
      return <h2 key={i} className="text-xl sm:text-2xl font-bold tracking-tight mt-10 mb-3">{block.value}</h2>;
    case 'h3':
      return <h3 key={i} className="text-base sm:text-lg font-bold mt-7 mb-2">{block.value}</h3>;
    case 'p':
      return <p key={i} className="text-[15px] sm:text-base text-foreground/85 leading-[1.75] mb-4">{block.value}</p>;
    case 'ul':
      return (
        <ul key={i} className="list-disc pl-6 space-y-1.5 mb-5 text-[15px] sm:text-base text-foreground/85 leading-[1.7]">
          {(block.value || []).map((item, j) => <li key={j}>{item}</li>)}
        </ul>
      );
    case 'ol':
      return (
        <ol key={i} className="list-decimal pl-6 space-y-1.5 mb-5 text-[15px] sm:text-base text-foreground/85 leading-[1.7]">
          {(block.value || []).map((item, j) => <li key={j}>{item}</li>)}
        </ol>
      );
    case 'callout':
      return (
        <aside key={i} className="border-l-4 border-primary bg-primary/5 px-5 py-4 rounded-r-md text-[14px] sm:text-[15px] text-foreground/90 mb-6">
          {block.value}
        </aside>
      );
    case 'quote':
      return (
        <blockquote key={i} className="border-l-4 border-border italic text-foreground/70 px-5 py-2 my-6">
          {block.value}
        </blockquote>
      );
    case 'cta':
      return (
        <div key={i} className="mt-10 rounded-xl border border-border bg-card px-6 py-6 sm:py-7 text-center" data-testid="post-inline-cta">
          <p className="text-sm sm:text-base font-semibold mb-3">{block.value}</p>
          <Link to="/" className="inline-flex items-center gap-1.5 px-5 h-10 rounded-md bg-primary text-primary-foreground text-sm font-semibold shadow-lg shadow-primary/25">
            Start Free Trial <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      );
    default:
      return null;
  }
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = getPostBySlug(slug);
  if (!post) return <Navigate to="/blog" replace />;

  const allPosts = getAllPosts();
  const otherPosts = allPosts.filter(p => p.slug !== slug).slice(0, 2);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.description,
    image: [post.coverImage],
    datePublished: post.datePublished,
    dateModified: post.datePublished,
    author: { '@type': 'Organization', name: post.author || 'RoofMate' },
    publisher: {
      '@type': 'Organization',
      name: 'RoofMate',
      logo: { '@type': 'ImageObject', url: 'https://roofmate.app/logo192.png' },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `https://roofmate.app/blog/${post.slug}` },
    keywords: post.keywords,
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://roofmate.app/' },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://roofmate.app/blog' },
      { '@type': 'ListItem', position: 3, name: post.title, item: `https://roofmate.app/blog/${post.slug}` },
    ],
  };

  return (
    <div className="min-h-screen bg-background" data-testid={`blog-post-${post.slug}`}>
      <Seo
        title={post.title}
        description={post.description}
        path={`/blog/${post.slug}`}
        keywords={post.keywords}
        image={post.coverImage}
        jsonLd={[articleJsonLd, breadcrumbJsonLd]}
      />

      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-card/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center cursor-pointer">
            <img src="/logo-full.png" alt="RoofMate logo" className="h-8 sm:h-[2.4rem] object-contain" width="180" height="40" />
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/blog" className="text-sm font-medium text-muted-foreground hover:text-foreground">Blog</Link>
            <Button onClick={() => navigate('/login')} variant="outline" className="rounded-md font-medium text-sm h-9 px-4">Sign In</Button>
            <Button onClick={() => navigate('/')} className="rounded-md font-medium text-sm h-9 px-4">Get Started</Button>
          </div>
        </div>
      </nav>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs text-muted-foreground mb-6">
          <Link to="/" className="hover:text-foreground">Home</Link>
          <CaretRight size={11} weight="bold" />
          <Link to="/blog" className="hover:text-foreground">Blog</Link>
          <CaretRight size={11} weight="bold" />
          <span className="truncate text-foreground/70">{post.title}</span>
        </nav>

        <header className="mb-8">
          <div className="flex items-center gap-2 mb-4 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
            <span>{new Date(post.datePublished).toLocaleDateString('en-AU', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
            <span aria-hidden="true">·</span>
            <span>{post.readingTime}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-[2.6rem] font-bold tracking-tight leading-[1.15]">{post.title}</h1>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground">{post.description}</p>
        </header>

        <div className="rounded-xl overflow-hidden mb-10 bg-secondary">
          <img
            src={post.coverImage}
            alt={post.coverAlt}
            className="w-full aspect-[16/9] object-cover"
            loading="eager"
            fetchpriority="high"
            width="1600"
            height="900"
          />
        </div>

        <div className="prose-roofmate">
          {post.body.map((block, i) => renderBlock(block, i))}
        </div>

        {/* Back to blog */}
        <div className="mt-12 pt-6 border-t border-border">
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">
            <ArrowLeft size={14} weight="bold" /> Back to all articles
          </Link>
        </div>
      </article>

      {/* Related */}
      {otherPosts.length > 0 && (
        <section className="border-t border-border bg-card">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
            <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-5">Keep reading</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {otherPosts.map(p => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="group block bg-background rounded-lg overflow-hidden border border-border hover:border-primary/40 transition-all">
                  <div className="aspect-[16/9] overflow-hidden bg-secondary">
                    <img src={p.coverImage} alt={p.coverAlt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  </div>
                  <div className="p-5">
                    <h3 className="font-bold text-base leading-snug group-hover:text-primary transition-colors">{p.title}</h3>
                    <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{p.description}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
